// UC25 — the vocabulary the Agregări screens speak, and the reads behind them.
// The administrator picks a source, dimensions and results; nothing in these
// screens names a PostgreSQL column, a view or an interval literal.
// Reference: docs/caiet-uc-spec.md §UC25 §UC-AGG-01..11;
// wireframes/react/src/pages/AdminAgregari.tsx.
import { apiClient } from '../../app/api-client';

export type AggregateStatus = 'draft' | 'active' | 'updating' | 'failed' | 'archived';
export type AggregateSource = 'stock_movements' | 'aggregate';
export type AggregateFunction = 'SUM' | 'COUNT' | 'AVG' | 'MIN' | 'MAX' | 'MEDIAN';
export type ExpressionTemplate = 'ratio' | 'difference';

export interface StandardCalculation {
  kind?: 'standard';
  fn: AggregateFunction;
  column: string;
  alias: string;
}

export interface ExpressionCalculation {
  kind: 'expression';
  template: ExpressionTemplate;
  firstOperand: string;
  secondOperand: string;
  alias: string;
}

export type Calculation = StandardCalculation | ExpressionCalculation;

export interface AggregateView {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  source: AggregateSource;
  baseAggregate: string | null;
  bucketInterval: string;
  groupBy: string[];
  calculations: Calculation[];
  excludeExpiredLots: boolean;
  retentionInterval: string | null;
  refreshScheduleInterval: string;
  status: AggregateStatus;
  scheduled: boolean;
  lastRefreshAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  // caiet UC-AGG-11: one of the aggregations the system is configured with,
  // agreed with the beneficiary. Inactivate it, never delete it.
  predefined: boolean;
}

// caiet UC-AGG-07 / UC-AGG-08 — one row of the fișa's Istoric section.
export type AggregateHistoryEventKind =
  | 'CREATED'
  | 'DEFINITION_UPDATED'
  | 'REFRESH'
  | 'REBUILD'
  | 'SCHEDULED_REFRESH'
  | 'CORRECTION_REFRESH'
  | 'ACTIVATED'
  | 'DEACTIVATED';

export interface AggregateHistoryEntry {
  id: string;
  event: AggregateHistoryEventKind;
  status: 'SUCCEEDED' | 'FAILED';
  rangeStart: string;
  rangeEnd: string;
  detail: string;
  actorId: string;
  actorRole: string;
  recordedAt: string;
}

// The fișa's "Efectuat de" cell (AdminAgregari.tsx:1780) names the administrator by the masked
// IDNP the server sends, else by their role, through the one attribution renderer
// (`HistoryActor`). The trail stores and returns no name (CF-63, SEC-14, BUG-1-009). A row with
// neither is the scheduler's own run, which the cell names as the system, because there the
// system really did it.
export function hasRecordedActor(
  entry: Pick<AggregateHistoryEntry, 'actorId' | 'actorRole'>,
): boolean {
  return Boolean(entry.actorId || entry.actorRole);
}

export interface AggregateHistoryPage {
  entries: AggregateHistoryEntry[];
  total: number;
}

export interface AggregateDependency {
  kind: 'aggregate' | 'alert_rule' | 'dashboard' | 'stock_view';
  ref: string;
  label: string;
  active: boolean;
}

export interface AggregateRowsPage {
  rows: Record<string, unknown>[];
  total: number;
}

// Q-1-05, docs/shape-ops-agreed-aggregations.md D9: a stock aggregate keeps the net
// movement of each interval, and core adds to each of its rows `_level`, the stock at
// the end of that interval, the figure the stock screen shows (CF-41). `_` cannot
// start a result's name, so the key collides with none.
export const LEVEL_KEY = '_level';

export function carriesLevel(rows: readonly Record<string, unknown>[]): boolean {
  return rows.some((row) => typeof row[LEVEL_KEY] === 'number');
}

export interface AggregateWriteBody {
  name?: string;
  displayName: string;
  description: string;
  source: AggregateSource;
  baseAggregate?: string;
  bucketInterval: string;
  groupBy: string[];
  calculations: Calculation[];
  excludeExpiredLots: boolean;
  retentionInterval: string | null;
  refreshScheduleInterval: string;
}

// The bucket vocabulary of the wireframe: zi, săptămână, lună, an.
export const BUCKET_INTERVALS = ['1 day', '1 week', '1 month', '1 year'] as const;

// SETTLED ARBITRATION — 2026-09-01, spec 002 FR-016, recorded in
// specs/002-admin-interface-repairs/contracts/wireframe-divergences.md (W21).
// The wireframe offers six sources (AdminAgregari.tsx:52-79, admin-data.ts:358);
// the application offers these two, and that is where it stays. Not taste — the
// other four cannot be built against a data source that exists:
//
//  • Every aggregate SIMSM builds is a TimescaleDB continuous aggregate
//    (`WITH (timescaledb.continuous)`, apps/core-service/src/aggregate/sql-template.ts),
//    so its root must be a hypertable. `reporting_db` has exactly one: `core.stock`.
//  • "Rapoarte de stoc" would root at `core.submission`, a plain table that is the
//    FK target of `core.stock` itself (`fk_stock_submission`, present on every
//    chunk) and of `core.submission_idempotency`. TimescaleDB forbids a hypertable
//    being referenced by a foreign key, so offering this source means dropping the
//    link between a stock movement and the report it came from — the traceability
//    UC40/UC50 rest on. Its two meaningful measures are reachable today anyway:
//    "Rânduri raportate" is COUNT over the same movements, and "Corectări" is the
//    CORRECTION value of the `reason` dimension below.
//  • "Nomenclatoare", "Registratori" and "Locații de stocare" have no event time
//    at all: `core.dim_medicine`, `core.dim_registrar` and `core.dim_site` are
//    upserted current-state projections whose `updated_at` is a mutation stamp,
//    not a fact time, so a time_bucket over them would rewrite its own history on
//    every upsert. Their real change history lives in `catalog_db`, a different
//    database a continuous aggregate in `reporting_db` cannot read at all
//    (docs/shape-ops-arch-alignment.md §1.1, one database per owner service).
//
// UC-AGG-06 is permissive ("pot utiliza … prin tabele, vederi, materializări sau
// snapshot-uri analitice") and all ten UC-AGG-11 aggregates are buildable from
// these two, so nothing the caiet mandates is out of reach. Adding the other four
// is a data-platform change — a submission event stream and dimension snapshots —
// not a change to this list.
export const AGGREGATE_SOURCES: AggregateSource[] = ['stock_movements', 'aggregate'];

// Group-by vocabulary. Every id here is a column core-service publishes on the
// materialized view and admits in its allowlist (apps/core-service/src/aggregate/
// sql-template.ts STOCK_DIMENSIONS); adding one there is what makes it offerable.
//
// docs/shape-ops-agreed-aggregations.md D2: the codes (region, locality, unique
// medicine) are what an aggregate groups by; the names beside them only label them.
export const STOCK_DIMENSIONS = [
  'unique_medicine',
  'medicine_name',
  'medicine_code',
  'registrar_id',
  'registrar_kind',
  'site_name',
  'site_id',
  'region_id',
  'region_name',
  'locality_id',
  'locality_name',
  'reason',
  'lot',
  'lot_expiry',
] as const;

export const MAX_DIMENSIONS = 6;
export const MAX_CALCULATIONS = 8;

export interface MeasureOption {
  column: string;
  functions: AggregateFunction[];
}

const COUNTING: AggregateFunction[] = ['COUNT'];
const NUMERIC: AggregateFunction[] = ['SUM', 'AVG', 'MIN', 'MAX', 'MEDIAN'];

// What "Cantitate" and "Mișcări" mean over the stock movements. An aggregate over
// another aggregate measures that aggregate's own results instead.
// The three flows split each difference by its sign (CF-55); "consumed" keeps the
// dispensed and sold outflows (AG11).
export const STOCK_MEASURES: MeasureOption[] = [
  { column: 'quantity_signed', functions: NUMERIC },
  { column: 'quantity_in', functions: NUMERIC },
  { column: 'quantity_out', functions: NUMERIC },
  { column: 'quantity_consumed', functions: NUMERIC },
  { column: 'id', functions: COUNTING },
];

export function measuresFor(source: AggregateSource, base: AggregateView | null): MeasureOption[] {
  if (source === 'stock_movements') return STOCK_MEASURES;
  if (!base) return [];
  return base.calculations.map((calculation) => ({
    column: calculation.alias,
    functions: [...NUMERIC, 'COUNT'] as AggregateFunction[],
  }));
}

export function dimensionsFor(source: AggregateSource, base: AggregateView | null): string[] {
  if (source === 'stock_movements') return [...STOCK_DIMENSIONS];
  return base ? base.groupBy : [];
}

export const SCHEDULE_PRESETS = [
  'manual',
  '15 minutes',
  '1 hour',
  '6 hours',
  '1 day',
  '1 week',
  '1 month',
] as const;

export const RETENTION_PRESETS = ['90 days', '365 days', '730 days', 'permanent'] as const;

export const INTERVAL_UNITS = ['minutes', 'hours', 'days', 'weeks', 'months'] as const;
export const RETENTION_UNITS = ['days', 'months', 'years'] as const;

export function isPresetSchedule(value: string): boolean {
  return (SCHEDULE_PRESETS as readonly string[]).includes(value);
}

export function isPresetRetention(value: string | null): boolean {
  return value === null || (RETENTION_PRESETS as readonly string[]).includes(value);
}

// '30 days' -> { amount: '30', unit: 'days' }. Anything unparseable falls back to
// the preset default, which is what the caller renders.
export function splitInterval(value: string): { amount: string; unit: string } | null {
  const match = value.trim().match(/^(\d+)\s+([a-z]+)$/);
  return match ? { amount: match[1] as string, unit: match[2] as string } : null;
}

const DIACRITICS = /[̀-ͯ]/g;

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

// The result name the administrator gets for free, in the same shape the seeded
// aggregates use (cantitate_totala, numar_miscari).
export function suggestResultName(fn: AggregateFunction, measureLabel: string): string {
  const base = slugify(measureLabel) || 'valoare';
  if (fn === 'COUNT') return `numar_${base}`;
  if (fn === 'SUM') return `${base}_totala`;
  if (fn === 'AVG') return `${base}_medie`;
  if (fn === 'MIN') return `${base}_min`;
  if (fn === 'MAX') return `${base}_max`;
  return `${base}_mediana`;
}

export const RESULT_NAME_RE = /^[a-z][a-z0-9_]{0,39}$/;
export const AGGREGATE_NAME_RE = /^[a-z][a-z0-9_]{2,79}$/;

export function isExpression(calculation: Calculation): calculation is ExpressionCalculation {
  return calculation.kind === 'expression';
}

export function listAggregates(): Promise<AggregateView[]> {
  return apiClient
    .get<{ aggregates: AggregateView[] }>('/admin/aggregates')
    .then((response) => response.data.aggregates);
}

export function getAggregate(name: string): Promise<AggregateView> {
  return apiClient.get<AggregateView>(`/admin/aggregates/${name}`).then((r) => r.data);
}

export function getDependencies(name: string): Promise<AggregateDependency[]> {
  return apiClient
    .get<{ dependencies: AggregateDependency[] }>(`/admin/aggregates/${name}/dependencies`)
    .then((r) => r.data.dependencies);
}

export function getAggregateRows(
  name: string,
  limit: number,
  offset: number,
): Promise<AggregateRowsPage> {
  return apiClient
    .get<AggregateRowsPage>(`/admin/aggregates/${name}/rows`, { params: { limit, offset } })
    .then((r) => r.data);
}

// `range` is the fișa's period filter, inclusive ISO days. It is sent to the
// server rather than applied here: the trail is paged, so narrowing it in the
// browser would narrow one page instead of the history.
export function getAggregateHistory(
  name: string,
  limit: number,
  offset: number,
  range: { from: string; to: string } = { from: '', to: '' },
): Promise<AggregateHistoryPage> {
  return apiClient
    .get<AggregateHistoryPage>(`/admin/aggregates/${name}/history`, {
      params: {
        limit,
        offset,
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
      },
    })
    .then((r) => r.data);
}
